/**
 * Setpoints the UI already shows before the device confirms them (e.g. right after a group
 * setpoint change). While a value is held, a websocket push that still carries the old value -
 * like a periodic refresh sent before the write reached the device - is shown with the held
 * value instead, so the display doesn't jump back and forth. A held value is released once the
 * server reports it, or when it expires.
 */

// The key an AC unit's setpoint is held under (UPDATE_UNIT carries it as data.setpoint)
export const AC_SETPOINT_KEY = 'setpoint';

interface HeldValue {
  value: any;
  expiresAt: number;
}

const held = new Map<string, HeldValue>();

const toHoldKey = (unitId: string, pointKey: string) => `${unitId}|${pointKey}`;

const isSameValue = (a: any, b: any) => String(a) === String(b);

export const holdSetpoint = (unitId: string, pointKey: string, value: any, durationMs: number) => {
  held.set(toHoldKey(unitId, pointKey), { value, expiresAt: Date.now() + durationMs });
};

export const releaseHeldSetpoints = (unitIds: string[]) => {
  const prefixes = unitIds.map((unitId) => `${unitId}|`);
  Array.from(held.keys()).forEach((key) => {
    if (prefixes.some((prefix) => key.startsWith(prefix))) {
      held.delete(key);
    }
  });
};

const resolveValue = (unitId: string, pointKey: string, incoming: any) => {
  const holdKey = toHoldKey(unitId, pointKey);
  const entry = held.get(holdKey);
  if (!entry) {
    return incoming;
  }
  if (Date.now() > entry.expiresAt || isSameValue(incoming, entry.value)) {
    held.delete(holdKey);
    return incoming;
  }
  return entry.value;
};

/** Returns the message with any held setpoints in place of the values it carries. */
export const applyHeldSetpoints = (message: any) => {
  const { name, data } = message || {};
  if (!held.size || !data?.unitId) {
    return message;
  }
  const unitId = String(data.unitId);

  if (name === 'UPDATE_UNIT' && AC_SETPOINT_KEY in data) {
    const setpoint = resolveValue(unitId, AC_SETPOINT_KEY, data.setpoint);
    return setpoint === data.setpoint ? message : { ...message, data: { ...data, setpoint } };
  }

  if ((name === 'UPDATE_BACNET_UNIT_DATA' || name === 'UPDATE_MODBUS_UNIT_DATA') && data.operationalData) {
    let changed = false;
    const operationalData = { ...data.operationalData };
    Object.keys(operationalData).forEach((pointKey) => {
      const value = resolveValue(unitId, pointKey, operationalData[pointKey]);
      if (value !== operationalData[pointKey]) {
        operationalData[pointKey] = value;
        changed = true;
      }
    });
    return changed ? { ...message, data: { ...data, operationalData } } : message;
  }

  return message;
};
